import { notify } from '@/lib/toast';
import { useState, useRef, useCallback, useEffect, useDeferredValue } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Package, ScanBarcode, CheckCircle2, Plus, TrendingUp, Store } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { posService } from '@/services/pos.service';
import { useCartStore } from '@/stores/cart.store';
import { useAuthStore } from '@/stores/auth.store';
import { useLocationStore } from '@/stores/location.store';
import { formatCurrency, cn } from '@/lib/utils';
import { FilterChips } from '@/components/ui/filter-chips';
import { resolveCategoryTileSrc } from '@/lib/category-tiles';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import type { Product, Category } from '@/types/pos';

/**
 * Product image tile (T-201). Always resolves to a bundled vector tile —
 * explicit product image → seeded category image → category-derived tile
 * (`categoryTileUrl` falls back to `default.svg` for unknown slugs), so a card is
 * never image-free and NEVER emoji (vector-only / no-emoji rule). On a load error
 * a lucide `Package` icon on a semantic tile is shown (state-driven, no innerHTML).
 * The hover "add" affordance lives inside the image wrapper so it tracks the image.
 */
function ProductImage({ product, showAdd }: { product: Product; showAdd: boolean }) {
  const [errored, setErrored] = useState(false);
  const src = product.imageUrl ?? resolveCategoryTileSrc(product.category?.imageUrl, product.category?.name ?? 'default');

  return (
    <div className="relative h-32 w-full overflow-hidden bg-muted/20">
      {errored ? (
        <div className="flex h-full w-full items-center justify-center bg-muted/40">
          <Package className="h-10 w-10 text-muted-foreground" aria-hidden />
        </div>
      ) : (
        <img
          src={src}
          alt={product.name}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.07]"
          loading="lazy"
          onError={() => setErrored(true)}
        />
      )}
      <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-scrim/25 to-transparent" />
      {showAdd && (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-2 end-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[var(--shadow-md)] opacity-0 translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0"
        >
          <Plus className="h-4 w-4" />
        </span>
      )}
    </div>
  );
}

/** POS-I57: SKU for a product added from the till (QA-<time><random>, upper case). */
function quickAddSku(): string {
  const time = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 5).toUpperCase();
  return `QA-${time}${rand}`;
}

export function ProductGrid() {
  const { t } = useTranslation('pos');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const [gridCols, setGridCols] = useState<3 | 4 | 5>(() => {
    const saved = localStorage.getItem('pos-grid-cols');
    return (saved ? Number(saved) : 4) as 3 | 4 | 5;
  });
  const handleGridCols = (cols: 3 | 4 | 5) => {
    setGridCols(cols);
    localStorage.setItem('pos-grid-cols', String(cols));
  };
  const gridColsClass = { 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4', 5: 'sm:grid-cols-5' }[gridCols];
  const [isScanning, setIsScanning] = useState(false);
  const [lastAdded, setLastAdded] = useState<{ name: string; qty: number } | null>(null);
  const [scanFocused, setScanFocused] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddPrice, setQuickAddPrice] = useState('');
  // POS-I77: the cashier picks the category in the Quick-add panel (defaults to the one being browsed).
  const [quickAddCategoryId, setQuickAddCategoryId] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const lastAddedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addItem = useCartStore((s) => s.addItem);
  const cartItems = useCartStore((s) => s.items);
  const customer = useCartStore((s) => s.customer);
  const { hasPermission } = useAuthStore();
  const canWriteProducts = hasPermission('products:write') || hasPermission('products:*');
  const queryClient = useQueryClient();
  const priceTierId = customer?.priceTierId ?? undefined;
  const { selectedLocationId } = useLocationStore();

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => posService.getCategories(),
    staleTime: 5 * 60 * 1000,
  });

  // POS-I77: the tenant's standard tax rate (the one flagged default under Tax Rates). A quick-added
  // product with no category taxes of its own is given this rate so it is not sold tax-free.
  const { data: defaultTaxRateId } = useQuery({
    queryKey: ['quick-add-default-tax-rate'],
    queryFn: () =>
      api
        .get('/tax-rates')
        .then((r) => {
          const rows = (Array.isArray(r.data) ? r.data : r.data?.data ?? []) as Array<{ id: string; isDefault?: boolean; isActive?: boolean }>;
          return rows.find((x) => x.isDefault && x.isActive !== false)?.id ?? null;
        })
        .catch(() => null),
    enabled: quickAddOpen,
    staleTime: 5 * 60 * 1000,
  });

  const quickAddMutation = useMutation({
    mutationFn: (data: { name: string; price: number; categoryId?: string; taxRateId?: string | null }) =>
      // POS-I57: the products API needs a SKU and calls the selling price salePrice. The panel only
      // asks for a name, a price and (POS-I77) a category, so the SKU is generated here (QA- + time +
      // random suffix: unique per till, and editable later under Inventory > Products).
      api
        .post('/products', {
          name: data.name,
          sku: quickAddSku(),
          salePrice: data.price,
          categoryId: data.categoryId ?? undefined,
          trackStock: false,
          // POS-I77: taxed like the rest of the shop - the category's taxes when it has any, else the
          // tenant's standard tax rate (set on the product itself).
          ...(data.taxRateId ? { taxOverride: true, taxRateIds: [data.taxRateId] } : {}),
        })
        .then((r) => ({ product: r.data, categoryId: data.categoryId })),
    onSuccess: ({ product, categoryId }) => {
      toast.success(`"${product.name}" added`);
      queryClient.invalidateQueries({ queryKey: ['pos-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setQuickAddOpen(false);
      setQuickAddName('');
      setQuickAddPrice('');
      // Auto-add to cart immediately
      if (product) {
        const category = categoryId ? categories.find((c: Category) => c.id === categoryId) ?? null : null;
        addItem({
          ...product,
          variants: [],
          category: category
            ? { id: category.id, name: category.name, imageUrl: category.imageUrl, skipPaymentMethodTax: category.skipPaymentMethodTax, taxes: category.taxes ?? [] }
            : null,
          taxes: product.taxes ?? [],
          productTaxes: [],
        });
      }
    },
    onError: (err) => notify.error(err, 'Failed to add product'),
  });

  const handleQuickAdd = () => {
    const price = parseFloat(quickAddPrice);
    if (!quickAddName.trim()) { toast.error('Product name is required'); return; }
    if (!price || price <= 0) { toast.error('Price must be greater than 0'); return; }
    const cat = quickAddCategoryId || undefined;
    const catRow = cat ? categories.find((c: Category) => c.id === cat) : undefined;
    const categoryHasTax = (catRow?.taxes?.length ?? 0) > 0;
    quickAddMutation.mutate({
      name: quickAddName.trim(),
      price,
      categoryId: cat,
      taxRateId: categoryHasTax ? null : defaultTaxRateId ?? null,
    });
  };

  // Debounce the search query so we don't fire an API call on every keystroke
  const deferredSearch = useDeferredValue(search);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['pos-products', deferredSearch, selectedCategory, priceTierId, selectedLocationId],
    queryFn: () => posService.getProducts(deferredSearch || undefined, selectedCategory, priceTierId, selectedLocationId ?? undefined),
    staleTime: 60 * 1000,
  });

  // T-134: Top products by 7-day sales velocity for one-tap quick-add
  const { data: topProducts = [] } = useQuery({
    queryKey: ['pos-top-velocity', priceTierId, selectedLocationId],
    queryFn: () => posService.getTopVelocityProducts(selectedLocationId ?? undefined, priceTierId, 20),
    staleTime: 5 * 60 * 1000,
  });
  const showVelocityStrip = !deferredSearch && !selectedCategory && topProducts.length > 0;

  // Return focus to scan input whenever user clicks elsewhere on the POS
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // Don't steal focus if user is clicking cart items or inputs
      if (target.closest('[data-cart-panel]') || target.closest('input') || target.closest('select')) return;
      requestAnimationFrame(() => searchRef.current?.focus());
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  /** Cart quantity for a product (sum across all variants in cart) */
  const getCartQty = useCallback((productId: string) =>
    cartItems.filter((i) => i.product.id === productId).reduce((sum, i) => sum + i.quantity, 0),
    [cartItems]
  );

  /** Show "last added" banner for 2 seconds */
  const showLastAdded = useCallback((name: string, totalQty: number) => {
    if (lastAddedTimer.current) clearTimeout(lastAddedTimer.current);
    setLastAdded({ name, qty: totalQty });
    lastAddedTimer.current = setTimeout(() => setLastAdded(null), 2000);
  }, []);

  const handleProductClick = useCallback((product: Product) => {
    if (product.variants.length > 0) {
      addItem(product, product.variants[0]);
    } else {
      addItem(product);
    }
    const cartQty = cartItems.filter((i) => i.product.id === product.id).reduce((s, i) => s + i.quantity, 0) + 1;
    showLastAdded(product.name, cartQty);
    requestAnimationFrame(() => searchRef.current?.focus());
  }, [addItem, cartItems, showLastAdded]);

  const handleScanOrSearch = useCallback(async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !search.trim()) return;
    e.preventDefault();

    const barcode = search.trim();

    // Fast path: check already-loaded products first (instant, no API call)
    const local = products.find((p: Product) => p.barcode === barcode);
    if (local) {
      handleProductClick(local);
      setSearch('');
      return;
    }

    // Slow path: call API for products not currently in the grid view
    setIsScanning(true);
    try {
      const product = await posService.getProductByBarcode(barcode);
      if (product) {
        handleProductClick(product);
        setSearch('');
      } else {
        toast.error('Product not found', { duration: 1800, icon: <Search className="h-4 w-4" /> });
      }
    } finally {
      setIsScanning(false);
      searchRef.current?.focus();
    }
  }, [search, products, handleProductClick]);

  return (
    <div className="flex h-full flex-col">
      {/* ── Scan / Search bar ── */}
      <div className="border-b px-3 py-2.5 bg-gradient-to-r from-primary/10 to-primary/10 ">
        <div className="relative">
          {isScanning ? (
            <ScanBarcode className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary-text animate-pulse" />
          ) : (
            <Search className={`absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors duration-200 ${scanFocused ? 'text-primary-text' : 'text-muted-foreground'}`} />
          )}
          <Input
            ref={searchRef}
            placeholder={t('searchProducts')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleScanOrSearch}
            onFocus={() => setScanFocused(true)}
            onBlur={() => setScanFocused(false)}
            className={`ps-9 pe-24 h-12 text-base transition-all duration-200 ${
              scanFocused
                ? 'border-primary/30 ring-1 ring-primary/30 '
                : ''
            }`}
            autoFocus
            data-pos-search
          />
          <div className="absolute end-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
            {scanFocused && !isScanning && (
              <span className="text-xs font-medium text-primary-text bg-primary/10 px-1.5 py-0.5 rounded border border-primary/30">
                {t('scanReady')}
              </span>
            )}
            {!scanFocused && (
              <span className="text-xs text-muted-foreground/50 font-mono">↵</span>
            )}
          </div>
        </div>

        {/* Last added banner */}
        {lastAdded && (
          <div className="mt-1.5 flex items-center gap-1.5 text-xs text-success-text animate-in fade-in slide-in-from-top-1 duration-200">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            <span className="font-medium truncate">{lastAdded.name}</span>
            <span className="text-success-text/70 shrink-0">× {lastAdded.qty}</span>
          </div>
        )}

        {/* Shortcut hints + grid density toggle */}
        <div className="mt-1.5 flex items-center justify-between">
          <div className="flex gap-3 text-xs text-muted-foreground">
            <span>
              <kbd className="px-1 py-0.5 rounded bg-muted border border-border font-mono text-xs">F2</kbd>{' '}Search
            </span>
            <span>
              <kbd className="px-1 py-0.5 rounded bg-muted border border-border font-mono text-xs">F12</kbd>{' '}Checkout
            </span>
          </div>
          <div className="flex items-center gap-1">
            {([3, 4, 5] as const).map((n) => (
              <button
                key={n}
                onClick={() => handleGridCols(n)}
                className={cn(
                  'min-h-[36px] min-w-[36px] rounded text-xs font-mono border transition-colors',
                  gridCols === n
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border text-muted-foreground hover:bg-muted',
                )}
                title={`${n} columns`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="border-b px-2 py-2 bg-muted/10">
        <FilterChips
          size="touch"
          chips={[
            { key: 'all', label: t('allProducts'), leading: <Store className="h-4 w-4 shrink-0" aria-hidden /> },
            ...categories.map((cat: Category) => ({
              key: cat.id,
              label: cat.name,
              // Always a bundled vector tile (categoryTileUrl → default.svg for unknown slugs) — no emoji.
              leading: (
                <img
                  src={resolveCategoryTileSrc(cat.imageUrl, cat.name)}
                  alt=""
                  className="h-4 w-4 rounded-full object-cover shrink-0"
                />
              ),
            })),
          ]}
          value={selectedCategory ?? 'all'}
          onChange={(key) => setSelectedCategory(key === 'all' ? undefined : key)}
        />
      </div>

      {/* ── Top sellers quick-add strip (7-day velocity) ── */}
      {showVelocityStrip && (
        <div className="border-b bg-warning/10 px-2 py-2">
          <p className="mb-1.5 flex items-center gap-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-warning-text">
            <TrendingUp className="h-3.5 w-3.5" /> {t('topSellers')}
          </p>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-0.5">
            {topProducts.map((product: Product) => {
              const qty = getCartQty(product.id);
              return (
                <button
                  key={product.id}
                  onClick={() => handleProductClick(product)}
                  className="relative flex shrink-0 flex-col items-start gap-0.5 rounded-lg border border-warning/30 bg-card min-h-[44px] px-3 text-start transition-all hover:border-warning/30 hover:shadow-sm active:scale-95"
                  title={product.name}
                >
                  <span className="max-w-[7.5rem] truncate text-sm font-semibold">{product.name}</span>
                  <span className="text-sm font-bold text-warning-text">{formatCurrency(product.salePrice)}</span>
                  {qty > 0 && (
                    <span className="absolute -end-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-xs font-bold text-primary-foreground">
                      {qty}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Product grid ── */}
      <div className="flex-1 overflow-auto p-2.5 bg-muted/5">
        {isLoading ? (
          <div className={cn('grid grid-cols-2 gap-2', gridColsClass)}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-xl border bg-card overflow-hidden animate-pulse">
                <div className="h-28 bg-muted" />
                <div className="p-2.5 space-y-1.5">
                  <div className="h-3 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                  <div className="h-4 bg-muted rounded w-1/3 mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-muted-foreground py-8">
            <Package className="mb-3 h-12 w-12 opacity-30" />
            <p className="text-sm font-semibold">{t('noProductsFound')}</p>
            <p className="text-xs opacity-70 mb-4">{t('noProductsHint')}</p>
            {canWriteProducts && !quickAddOpen && (
              <Button size="sm" variant="outline" onClick={() => { setQuickAddOpen(true); setQuickAddName(search); setQuickAddCategoryId(selectedCategory ?? ''); }} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                {t('quickAdd', { name: search || t('products') })}
              </Button>
            )}
            {quickAddOpen && (
              <div className="mt-3 w-full max-w-xs rounded-lg border bg-background p-4 shadow-md space-y-3">
                <p className="text-xs font-semibold text-foreground">{t('quickAddProduct')}</p>
                <Input
                  placeholder={t('products')}
                  value={quickAddName}
                  onChange={(e) => setQuickAddName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleQuickAdd()}
                />
                <Input
                  placeholder={t('sellingPrice')}
                  type="number"
                  min={1}
                  step={1}
                  value={quickAddPrice}
                  onChange={(e) => setQuickAddPrice(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleQuickAdd()}
                />
                <select
                  aria-label={t('category')}
                  value={quickAddCategoryId}
                  onChange={(e) => setQuickAddCategoryId(e.target.value)}
                  className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                >
                  <option value="">{t('quickAddNoCategory')}</option>
                  {categories.map((c: Category) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1" onClick={handleQuickAdd} disabled={quickAddMutation.isPending}>
                    {quickAddMutation.isPending ? t('adding') : t('addAndSell')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setQuickAddOpen(false)}>{t('cancel', { ns: 'common' })}</Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={cn('grid grid-cols-2 gap-2', gridColsClass)}>
            {products.map((product: Product) => (
              <ProductCard
                key={product.id}
                product={product}
                cartQty={getCartQty(product.id)}
                onClick={() => handleProductClick(product)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ProductCard({ product, cartQty, onClick }: { product: Product; cartQty: number; onClick: () => void }) {
  const isLowStock = product.trackStock && product.stockQuantity <= 5;
  const isOutOfStock = product.trackStock && product.stockQuantity <= 0;
  const isExpired = product.expiryTracking && product.hasExpiredBatches;
  const isDisabled = isOutOfStock || isExpired;

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={`group relative flex flex-col rounded-2xl border bg-card text-start transition-all duration-200 overflow-hidden
        ${isDisabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:border-primary/40 hover:shadow-[var(--shadow-md)] hover:-translate-y-0.5 active:scale-[0.98]'
        }
        ${cartQty > 0 ? 'border-primary/40 shadow-[var(--shadow-sm)]' : ''}`}
    >
      {/* Expired overlay badge */}
      {isExpired && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-destructive/10 rounded-xl">
          <span className="bg-destructive text-destructive-foreground text-xs font-bold px-2 py-1 rounded-md">EXPIRED</span>
        </div>
      )}

      {/* Cart quantity badge */}
      {cartQty > 0 && (
        <div className="absolute top-2 end-2 z-10 flex h-7 min-w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold shadow-md px-1.5">
          {cartQty}
        </div>
      )}

      {/* Image (hover "add" affordance lives inside the image wrapper — T-201) */}
      <ProductImage product={product} showAdd={!isDisabled} />

      {/* Info */}
      <div className="flex flex-col gap-0.5 p-2.5">
        <span className="text-sm font-semibold leading-tight line-clamp-2 text-foreground">
          {product.name}
        </span>

        <div className="mt-1 flex items-end justify-between gap-1">
          <span className="text-base font-bold text-primary-text">
            {formatCurrency(Number(product.salePrice))}
          </span>

          {product.trackStock && !isExpired && (
            <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-xs font-medium leading-none
              ${isOutOfStock
                ? 'bg-destructive/10 text-destructive-text'
                : isLowStock
                ? 'bg-warning/10 text-warning-text '
                : 'bg-success/10 text-success-text '
              }`}
            >
              {isOutOfStock ? 'Out' : `${product.stockQuantity} ${product.unit}`}
            </span>
          )}
        </div>

        {product.barcode && (
          <span className="mt-0.5 flex items-center gap-0.5 text-[10px] text-muted-foreground/50 font-mono">
            <ScanBarcode className="h-2.5 w-2.5 shrink-0" />
            {product.barcode}
          </span>
        )}
      </div>
    </button>
  );
}
